import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'src/store/store';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import styled from 'styled-components';
import { capitalize } from 'underscore.string';
import MobileMoreMenu from 'src/components/siteBar/mobileMoreMenu';
import i18n from 'src/utils/translate';
import { Section } from 'src/constants/footfall';
import { footfall } from 'src/components/siteBar/sitebarFootfall';
import { Counter, ImageContainer, LinkText, SiteBar, StyledLink } from 'src/components/siteBar/sharedStyles';
import hub from 'public/static/img/icons/hub.svg';
import paperPlane from 'public/static/img/icons/paper-plane.svg';
import pen from 'public/static/img/icons/pen.svg';
import bell from 'public/static/img/icons/bell.svg';
import menu from 'public/static/img/icons/menu.svg';

export default function MobileSiteBarBottom(): JSX.Element {
  const { asPath } = useRouter();
  const route = asPath.split('/')[1];
  const unreadMessagesCount = useSelector(({ context }) => context?.unreadMessagesCount) ?? 0;
  const unreadNotificationsCount = useSelector(({ context }) => context.notifications?.unreadNotificationsCount) ?? 0;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const handleClickOutside = (event) => {
    if (!menuRef?.current?.contains(event.target)) setIsMenuOpen(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []); // eslint-disable-line

  return (
    <div ref={menuRef}>
      <BottomContainer>
        <Link
          legacyBehavior
          passHref
          as="/my-hub"
          href="/myHub"
        >
          <A
            $isActive={'my-hub' === route}
            onClick={() => {
              footfall(Section.MyHub);
              setIsMenuOpen(false);
            }}
          >
            <ImageContainer>
              <Image alt="My hub" src={hub} />
            </ImageContainer>
            <LinkText $isActive={'my-hub' === route}>{capitalize(i18n.t('my hub'))}</LinkText>
          </A>
        </Link>

        <Link
          legacyBehavior
          passHref
          as="/messages"
          href="/messages"
        >
          <A
            $isActive={'messages' === route}
            onClick={() => {
              footfall(Section.Chat);
              setIsMenuOpen(false);
            }}
          >
            <ImageContainer>
              <Image alt="Chat" src={paperPlane} />
              {unreadMessagesCount > 0 && <Counter data-testid="chat-message-number">{unreadMessagesCount}</Counter>}
            </ImageContainer>
            <LinkText $isActive={'messages' === route}>{capitalize(i18n.t('chat'))}</LinkText>
          </A>
        </Link>

        {/* Not using next/link here to force a page reload, easier than resetting state for the two editors when there are unsaved changes */}
        <A $isActive={asPath.includes('write')} href="/write" onClick={() => footfall(Section.POST)}>
          <ImageContainer>
            <Image alt="Post" src={pen} />
          </ImageContainer>
          <LinkText $isActive={asPath.includes('write')}>{capitalize(i18n.t('post'))}</LinkText>
        </A>

        <Link
          legacyBehavior
          passHref
          as="/notifications"
          href="/notifications"
        >
          <A
            $isActive={'notifications' === route}
            onClick={() => {
              footfall(Section.Alerts);
              setIsMenuOpen(false);
            }}
          >
            <ImageContainer>
              <Image alt="Alerts" src={bell} />
              {unreadNotificationsCount > 0 && <Counter>{unreadNotificationsCount}</Counter>}
            </ImageContainer>
            <LinkText $isActive={'notifications' === route}>{capitalize(i18n.t('alerts'))}</LinkText>
          </A>
        </Link>

        <A
          $isActive={isMenuOpen}
          href="#"
          ref={menuButtonRef}
          onClick={(e) => {
            e.preventDefault();
            setIsMenuOpen(!isMenuOpen);
            menuButtonRef?.current?.blur();
            footfall(Section.More);
          }}
        >
          <ImageContainer>
            <Image alt="Menu" src={menu} />
          </ImageContainer>
          <LinkText $isActive={isMenuOpen}>{capitalize(i18n.t('menu'))}</LinkText>
        </A>
      </BottomContainer>

      <MobileMoreMenu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
    </div>
  );
}

const BottomContainer = styled(SiteBar)`
  top: auto;
  bottom: 0;
  border-top: ${({ theme }) => theme.greyLine};
  border-bottom: 0;
  padding-bottom: env(safe-area-inset-bottom);
  height: calc(${({ theme }) => theme.navHeightTop} + env(safe-area-inset-bottom));
`;

const A = styled(StyledLink)`
  width: 20%;
`;
